// src/web.ts
var analyticsAddressReadyEvent = "hereby:analytics-address-ready";
function hasSafeAnalyticsAddress(win) {
  try {
    const url = new URL(win.location.href);
    const sensitive = /^(?:token|access_token|refresh_token|id_token|auth_token|authorization)$/i;
    const fragment = url.hash.slice(1).split("?").pop() ?? "";
    return !(url.username || url.password || [...url.searchParams.keys(), ...new URLSearchParams(fragment).keys()].some(
      (key) => sensitive.test(key)
    ));
  } catch {
    return false;
  }
}
function clearRetainedAuthToken(win) {
  try {
    const url = new URL(win.location.href);
    if (!url.searchParams.has("token")) {
      return;
    }
    url.searchParams.delete("token");
    win.history.replaceState(win.history.state, "", `${url.pathname}${url.search}${url.hash}`);
    win.dispatchEvent(new Event(analyticsAddressReadyEvent));
  } catch {
  }
}
var transports = /* @__PURE__ */ new WeakMap();
var missingFields = {
  app_module: null,
  surface: null,
  ui_screen: null,
  workflow: null,
  result: null,
  error_category: null,
  schema_version: null,
  release_version: null,
  duration_ms: null,
  interaction_code: null,
  tenant_key: null,
  page_location: null,
  page_title: null,
  page_referrer: null
};
function createWebTransport(options = {}) {
  const win = options.window ?? (typeof window !== "undefined" ? window : void 0);
  const existing = win && transports.get(win);
  if (existing) {
    return existing;
  }
  const addressReady = () => {
    try {
      return !!win && hasSafeAnalyticsAddress(win) && (options.addressReady?.() ?? true);
    } catch {
      return false;
    }
  };
  let settings;
  let consent = "denied";
  let ready = false;
  let load;
  let loaded = false;
  let failed = false;
  let container;
  const disable = (value) => {
    if (win && settings) {
      win[`ga-disable-${settings.destination.measurementId}`] = value;
    }
  };
  const push = (value) => {
    if (win) {
      win.dataLayer ??= [];
      win.dataLayer.push(value);
    }
  };
  const consentState = () => ({
    analytics_storage: consent,
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    policy_version: settings?.config.consentPolicyVersion ?? ""
  });
  const revoke = () => {
    consent = "denied";
    ready = false;
    disable(true);
    if (win?.dataLayer) {
      for (let i = win.dataLayer.length - 1; i >= 0; i--) {
        const entry = win.dataLayer[i];
        if (entry.event === "hereby_analytics" || entry.event === "hereby_analytics_ready") {
          win.dataLayer.splice(i, 1);
        } else if (entry.event === "hereby_analytics_consent") {
          entry.analytics_consent = consentState();
        }
      }
    }
    if (settings) {
      push({ event: "hereby_analytics_consent", analytics_consent: consentState() });
    }
  };
  const transport = {
    environment: () => ({
      host: win?.location.hostname ?? "",
      protocol: win?.location.protocol ?? "",
      surface: "web"
    }),
    setConsent(state, next) {
      if (next) {
        if (container && container !== next.destination.gtmId) {
          revoke();
          throw new Error("\uBD84\uC11D \uC804\uC1A1 \uB300\uC0C1\uC774 \uC77C\uCE58\uD558\uC9C0 \uC54A\uC544\uC694.");
        }
        settings = next;
      }
      if (state === "denied") {
        revoke();
        return;
      }
      consent = "granted";
      push({ event: "hereby_analytics_consent", analytics_consent: consentState() });
    },
    revoke,
    resetContext() {
      push({ analytics: null });
    },
    async initialize(next, guard) {
      if (!(win && guard()) || consent !== "granted" || !next.initialPage || failed || !addressReady()) {
        return;
      }
      if (container && container !== next.destination.gtmId) {
        revoke();
        return;
      }
      settings = next;
      container = next.destination.gtmId;
      if (!load) {
        disable(false);
        load = new Promise((resolve) => {
          const script = win.document.createElement("script");
          script.async = true;
          script.referrerPolicy = "no-referrer";
          script.src = `https://www.googletagmanager.com/gtm.js?id=${next.destination.gtmId}`;
          script.onload = () => {
            loaded = true;
            resolve();
          };
          script.onerror = () => {
            failed = true;
            disable(true);
            resolve();
          };
          push({ "gtm.start": Date.now(), event: "gtm.js" });
          win.document.head.append(script);
        });
      }
      await load;
      if (!addressReady()) {
        ready = false;
        disable(true);
        return;
      }
      if (!guard() || consent !== "granted" || !loaded || failed || ready) {
        return;
      }
      disable(false);
      ready = true;
      push({
        event: "hereby_analytics_ready",
        analytics_config: {
          schema_version: 1,
          group: next.config.group,
          app_module: next.config.appModule,
          surface: next.config.surface,
          release_version: next.config.releaseVersion,
          measurement_id: next.destination.measurementId,
          consent_policy_version: next.config.consentPolicyVersion,
          ...next.initialPage
        },
        analytics_consent: consentState()
      });
    },
    send(event, guard) {
      if (win && consent === "granted" && ready && guard() && addressReady()) {
        push({
          event: "hereby_analytics",
          analytics: {
            event_name: event.event_name,
            params: Object.fromEntries(
              Object.keys(missingFields).map((key) => [
                key,
                event.params[key] ?? null
              ])
            )
          }
        });
      }
    }
  };
  if (win) {
    transports.set(win, transport);
  }
  return transport;
}
export {
  analyticsAddressReadyEvent,
  clearRetainedAuthToken,
  createWebTransport,
  hasSafeAnalyticsAddress
};
//# sourceMappingURL=web.js.map